<template>
  <div
    :data-testid="dataTestid"
    class="w-full text-center"
    :class="pinCta ? 'flex h-full flex-col md:h-auto md:block' : ''"
  >
    <!--
      Left-aligned explicitly: the root is text-center for the code/title block, which
      would otherwise centre an inline-flex Back control too. A back affordance belongs at
      the start of the line — the convention everywhere, and what a mobile/app header does.
    -->
    <div v-if="$slots.back" class="text-left">
      <slot name="back" />
    </div>

    <!-- Spacing-Molecule-gap (16px) between every section, per design spec. -->
    <div class="flex flex-col items-center gap-4">
      <slot name="title">
        <h2 class="text-semantic-sizing-headline-sm font-semibold text-semantic-surface-on-surface">
          {{ title }}
        </h2>
      </slot>

      <div class="flex flex-col gap-4">
        <slot
          name="description"
          :destination="destination"
        >
          <!-- Instruction and destination on separate lines, per design. -->
          <p class="text-semantic-sizing-body-sm font-normal text-semantic-primary-variant-dark">
            {{ sentToLabel }}
            <span
              v-if="destination"
              class="block break-all font-bold text-semantic-primary-variant-dark"
            >
              {{ destination }}
            </span>
          </p>
        </slot>

        <slot
          name="expiry"
          :expired="isExpired"
          :label="expiryDisplayLabel"
        >
          <p
            v-if="expiryDisplayLabel"
            class="text-semantic-sizing-body-sm font-normal"
            :class="isExpired ? 'text-semantic-states-error-default' : 'text-semantic-surface-on-surface-variant'"
          >
            {{ expiryDisplayLabel }}
          </p>
        </slot>
      </div>

      <V2AtomsOtpInput
        ref="otpInputRef"
        v-model="code"
        class="w-full"
        :length="otpLength"
        :disabled="disabled"
        :error="!!error"
        data-testid="otp-verification-code-input"
        @complete="handleComplete"
      />

      <p
        v-if="error"
        role="alert"
        data-testid="otp-verification-error"
        class="text-sm text-semantic-states-error-default"
      >
        {{ error }}
      </p>

      <slot
        name="resend"
        :can-resend="canResend"
        :seconds-remaining="resendSecondsRemaining"
        :resend="triggerResend"
      >
        <p
          v-if="!canResend"
          class="text-semantic-sizing-body-sm font-normal text-semantic-surface-on-surface"
        >
          {{ resendCountingLabel(resendSecondsRemaining) }}
        </p>
        <V2AtomsButton
          v-else
          id="otp_verification_resend_btn"
          theme="tertiary"
          size="small"
          type="button"
          enable-tracking
          data-testid="otp-verification-resend-btn"
          :disabled="disabled"
          @click="triggerResend"
        >
          {{ resendLabel }}
        </V2AtomsButton>
      </slot>

      <slot
        v-if="showCantAccess"
        name="cant-access"
      >
        <V2AtomsButton
          id="otp_verification_cant_access_btn"
          theme="tertiary"
          size="small"
          type="button"
          enable-tracking
          data-testid="otp-verification-cant-access-btn"
          @click="emit('cant-access-click')"
        >
          {{ cantAccessText }}
        </V2AtomsButton>
      </slot>
    </div>

    <slot name="footer">
      <V2AtomsButton
        id="otp_verification_verify_btn"
        data-testid="otp-verification-verify-btn"
        type="button"
        enable-tracking
        size="base"
        :class="pinCta ? 'mt-auto md:mt-4' : 'mt-4'"
        full
        :disabled="disabled || code.length !== otpLength || isSubmitting"
        @click="handleVerifyClick"
      >
        {{ isSubmitting ? verifyingLabel : verifyLabel }}
      </V2AtomsButton>
    </slot>
  </div>
</template>

<script setup>
import { formatMmSs } from '~/utils/time/duration'

defineOptions({
  name: 'V2MoleculesOtpVerification'
})

const props = defineProps({
  /*
   * Drops the verify CTA to the bottom of the screen on small viewports, for the full-page
   * enrolment the mobile app renders. Opt-in because this molecule is also the login
   * challenge and the account-settings re-auth, which sit inside other content and must
   * keep the CTA in normal flow.
   */
  pinCta: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'Confirm it\'s you'
  },
  // The masked/partial value shown to the user, e.g. "04•• ••• 560" or "j***@mail.com".
  destination: {
    type: String,
    default: ''
  },
  sentToLabel: {
    type: String,
    default: 'We sent a 6-digit code to'
  },
  otpLength: {
    type: Number,
    default: 6
  },
  disabled: {
    type: Boolean,
    default: false
  },
  isSubmitting: {
    type: Boolean,
    default: false
  },
  // Server/validation error shown under the field (also flips the boxes to the error style).
  error: {
    type: String,
    default: ''
  },
  verifyLabel: {
    type: String,
    default: 'Verify'
  },
  verifyingLabel: {
    type: String,
    default: 'Verifying...'
  },
  // Auto-fires 'verify' the moment the last digit is entered, instead of waiting for the button.
  autoSubmitOnComplete: {
    type: Boolean,
    default: false
  },
  // Static text used when expirySeconds isn't provided, e.g. "It expires in 5 minutes."
  expiryLabel: {
    type: String,
    default: ''
  },
  // When set, runs a live countdown and formats it through expiryCountingLabel/expiredLabel instead.
  expirySeconds: {
    type: Number,
    default: null
  },
  expiryCountingLabel: {
    type: Function,
    default: seconds => `It expires in ${formatMmSs(seconds)}.`
  },
  expiredLabel: {
    type: String,
    default: 'This code has expired.'
  },
  resendSeconds: {
    type: Number,
    default: 60
  },
  resendLabel: {
    type: String,
    default: 'Resend code'
  },
  resendCountingLabel: {
    type: Function,
    default: seconds => `Resend code in ${formatMmSs(seconds)}`
  },
  showCantAccess: {
    type: Boolean,
    default: true
  },
  cantAccessText: {
    type: String,
    default: 'Can\'t access this number?'
  },
  dataTestid: {
    type: String,
    default: 'v2-molecules-otp-verification'
  }
})

const emit = defineEmits(['update:modelValue', 'verify', 'resend', 'complete', 'expired', 'cant-access-click'])

const code = defineModel({
  type: String,
  default: ''
})

const otpInputRef = ref()

// Resend countdown
const resendSecondsRemaining = ref(props.resendSeconds)
const canResend = computed(() => resendSecondsRemaining.value <= 0)
let resendTimer = null

function startResendTimer () {
  clearInterval(resendTimer)
  resendSecondsRemaining.value = props.resendSeconds
  if (props.resendSeconds <= 0) return

  resendTimer = setInterval(() => {
    resendSecondsRemaining.value -= 1
    if (resendSecondsRemaining.value <= 0) {
      clearInterval(resendTimer)
    }
  }, 1000)
}

function triggerResend () {
  if (!canResend.value || props.disabled) return
  emit('resend')

  /*
   * Clear what was typed. A resend replaces the code that was just rejected, so leaving the
   * old digits in the boxes invites the user to submit them again — and the expiry
   * countdown belongs to a code that has been superseded. reset() clears the field and
   * restarts both timers, which is why it stands in for the startResendTimer() call that
   * used to be here.
   */
  reset()
}

// Expiry countdown (optional — only runs when expirySeconds is provided)
const expirySecondsRemaining = ref(props.expirySeconds)
const isExpired = computed(() => props.expirySeconds !== null && expirySecondsRemaining.value <= 0)
let expiryTimer = null

function startExpiryTimer () {
  clearInterval(expiryTimer)
  if (props.expirySeconds === null) return

  expirySecondsRemaining.value = props.expirySeconds
  expiryTimer = setInterval(() => {
    expirySecondsRemaining.value -= 1
    if (expirySecondsRemaining.value <= 0) {
      clearInterval(expiryTimer)
      emit('expired')
    }
  }, 1000)
}

const expiryDisplayLabel = computed(() => {
  if (props.expirySeconds === null) return props.expiryLabel
  return isExpired.value ? props.expiredLabel : props.expiryCountingLabel(expirySecondsRemaining.value)
})

function handleComplete (value) {
  emit('complete', value)
  if (props.autoSubmitOnComplete) {
    emit('verify', value)
  }
}

function handleVerifyClick () {
  if (code.value.length !== props.otpLength) return
  emit('verify', code.value)
}

function reset () {
  code.value = ''
  otpInputRef.value?.clear()
  startResendTimer()
  startExpiryTimer()
}

defineExpose({ reset, restartResendTimer: startResendTimer, restartExpiryTimer: startExpiryTimer })

onMounted(() => {
  startResendTimer()
  startExpiryTimer()
})

onBeforeUnmount(() => {
  clearInterval(resendTimer)
  clearInterval(expiryTimer)
})
</script>
